/**
 * SidebarToolItem Component (Parametric Animation Refactor)
 *
 * Draggable tool item in the sidebar.
 *
 * Features:
 * - useDrag hook for drag functionality
 * - Drag type: "SIDEBAR_TOOL"
 * - Payload: tool definition from toolStore
 * - Custom drag preview: semi-transparent tool block
 * - Preview shows expected duration width
 * - Color-coded background by body part
 * - Hover state with Framer Motion
 * - Click to preview tool in 3D
 * - Preview indicator when tool is being previewed
 * - Radix Tooltip on hover showing body part and default easing
 */

import { useDrag } from 'react-dnd';
import { motion } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Eye } from 'lucide-react';
import type { ToolDefinition, BodyPart } from '../../stores/toolStore';
import { getBodyPartColor as getColor } from '../../utils/bodyPartColors';
import { DND_ITEM_TYPES } from './dndTypes';
import type { SidebarToolDragItem } from './dndTypes';

interface SidebarToolItemProps {
  /**
   * Tool definition from toolStore
   */
  tool: ToolDefinition;

  /**
   * Optional click handler for preview
   */
  onClick?: (toolId: string) => void;

  /**
   * Whether this tool is currently being previewed
   */
  isPreviewing?: boolean;
}

/**
 * Get background color for body part
 */
function getBodyPartColor(bodyPart: BodyPart): string {
  return getColor(bodyPart);
}

/**
 * Get hover color for body part
 * Lightens the base color by 10%
 */
function getBodyPartHoverColor(bodyPart: BodyPart): string {
  const baseColor = getColor(bodyPart);
  // Convert hex to RGB and lighten
  const hex = baseColor.replace('#', '');
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + 25);
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + 25);
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + 25);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Get display label for body part
 */
function getBodyPartLabel(bodyPart: BodyPart): string {
  const labels: Record<BodyPart, string> = {
    head: 'Head',
    body: 'Body',
    leftArm: 'Left Arm',
    rightArm: 'Right Arm',
    leftLeg: 'Left Leg',
    rightLeg: 'Right Leg',
    control: 'Control',
  };
  return labels[bodyPart];
}

/**
 * Get display label for easing function
 */
function getEasingLabel(easing: string): string {
  const labels: Record<string, string> = {
    linear: 'Linear',
    'ease-in': 'Ease In',
    'ease-out': 'Ease Out',
    'ease-in-out': 'Ease In-Out',
  };
  return labels[easing] ?? easing;
}

export function SidebarToolItem({
  tool,
  onClick,
  isPreviewing = false,
}: SidebarToolItemProps) {
  // Set up drag behavior
  const [{ isDragging }, drag] = useDrag<
    SidebarToolDragItem,
    unknown,
    { isDragging: boolean }
  >(
    () => ({
      type: DND_ITEM_TYPES.SIDEBAR_TOOL,
      item: {
        type: DND_ITEM_TYPES.SIDEBAR_TOOL,
        toolDefinition: tool,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [tool]
  );

  const backgroundColor = getBodyPartColor(tool.bodyPart);
  const hoverColor = getBodyPartHoverColor(tool.bodyPart);

  const handleClick = () => {
    if (onClick) {
      onClick(tool.id);
    }
  };

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <motion.div
          ref={drag as any}
          className="relative cursor-move select-none rounded p-3 mb-2"
          style={{
            backgroundColor,
            opacity: isDragging ? 0.5 : 1,
            boxShadow: isPreviewing
              ? '0 0 0 2px rgba(255, 255, 255, 0.5), var(--shadow-md)'
              : 'var(--shadow-sm)',
          }}
          whileHover={{
            scale: 1.02,
            backgroundColor: hoverColor,
            boxShadow: isPreviewing
              ? '0 0 0 2px rgba(255, 255, 255, 0.5), var(--shadow-md)'
              : 'var(--shadow-md)',
            transition: { duration: 0.15 },
          }}
          whileTap={{
            scale: 0.98,
            transition: { duration: 0.1 },
          }}
          onClick={handleClick}
          data-testid={`sidebar-tool-${tool.id}`}
          data-tool-id={tool.id}
          data-previewing={isPreviewing}
        >
          {/* Tool name */}
          <div
            className="text-sm font-medium"
            style={{
              color: 'rgba(255, 255, 255, 0.95)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            }}
          >
            {tool.name}
          </div>

          {/* Tool metadata */}
          <div
            className="text-xs mt-1"
            style={{
              color: 'rgba(255, 255, 255, 0.75)',
            }}
          >
            {tool.defaultDuration.toFixed(1)}s
          </div>

          {/* Preview indicator */}
          {isPreviewing && (
            <div
              className="absolute left-2 top-1/2 -translate-y-1/2"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              <Eye className="h-3 w-3" />
            </div>
          )}

          {/* Drag indicator icon */}
          <div
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-40"
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="5" cy="4" r="1.5" />
              <circle cx="11" cy="4" r="1.5" />
              <circle cx="5" cy="8" r="1.5" />
              <circle cx="11" cy="8" r="1.5" />
              <circle cx="5" cy="12" r="1.5" />
              <circle cx="11" cy="12" r="1.5" />
            </svg>
          </div>
        </motion.div>
      </Tooltip.Trigger>

      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={5}
          className="bg-dark-bg border panel-border rounded-lg px-3 py-2 text-sm text-dark-text shadow-lg z-50 max-w-xs"
          style={{ boxShadow: 'var(--shadow-lg)' }}
          data-testid={`tooltip-${tool.id}`}
        >
          <div className="space-y-1">
            <div className="font-semibold">{tool.name}</div>
            <div className="text-xs text-dark-text-muted space-y-0.5">
              <div>Body Part: {getBodyPartLabel(tool.bodyPart)}</div>
              <div>Default Duration: {tool.defaultDuration.toFixed(1)}s</div>
              <div>Default Easing: {getEasingLabel(tool.defaultEasing)}</div>
            </div>
          </div>
          <Tooltip.Arrow className="fill-dark-bg" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
